import { Button, Dialog, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useToken } from "../../server/auth/use-token";
import { extractMergeTags } from "../../utils/extract-merge-tags";
import { useDialog } from "../../utils/hooks/use-dialog";
import { notEmpty } from "../../utils/not-empty";
import { getRenderUrlCloudflareStream } from "../edit-content/hooks/use-save-asset-callback";
import { LoadingModal } from "../edit-content/loading-modal";
import { DesignHuddleEditor } from "./design-huddle-editor";
import { ErrorModal } from "./error-modal";
import { useDesignHuddle } from "./use-design-huddle";
import { useGetDesignHuddleAccessToken } from "./use-design-huddle-access-token";
import { useDesignHuddleProjectClone } from "./use-design-huddle-project-clone";
import { useDesignHuddlePublish } from "./use-design-huddle-publish-callback";
import { copyImage, getDesignHuddleRender, imageURL } from "./utils";

import type { DesignHuddleCustomisations } from "../message/editor/nodes/design-huddle-node";
import type { Editor, ElementProperties, Page } from "./types";
import type { FC } from "react";

const isTextElement = (
  element: ElementProperties,
): element is ElementProperties & { text: string } => Object.hasOwn(element, "text");

export const DesignHuddleModal: FC<{
  open: boolean;
  setOpen: (v: boolean) => void;
  project_id: string;
  account_id: string;
  onSave?: (data: {
    page?: number;
    clonedProjectId?: string;
    customisations: DesignHuddleCustomisations | undefined;
    publicRenderUrl?: string;
  }) => void;
  page_number?: number;
  page_id?: string;
  must_be_cloned?: boolean;
  onProgress?: (progress: number | null) => void;
  content_type: "video" | "image";
}> = ({
  open,
  setOpen,
  project_id,
  account_id,
  onSave,
  page_number,
  page_id,
  must_be_cloned,
  onProgress,
  content_type,
}) => {
  const [editor, setEditor] = useState<Editor>();
  const [pages, setPages] = useState<Page[]>();
  const [currentPage, setCurrentPage] = useState(page_number);
  const [currentProject, setCurrentProject] = useState<{ id: string; mustBeCloned: boolean }>({
    id: project_id,
    mustBeCloned: Boolean(must_be_cloned),
  });
  const [loading, setLoading] = useState(false);
  const [editorDirty, setEditorDirty] = useState(false);
  const errorDialog = useDialog();
  const editorDirtyRef = useRef(editorDirty);

  const getToken = useToken();
  const publishTemplate = useDesignHuddlePublish();
  const cloneProject = useDesignHuddleProjectClone();
  const getAccessToken = useGetDesignHuddleAccessToken();

  useEffect(() => {
    // Keep the ref updated so we always have the latest value to use in handleClose
    editorDirtyRef.current = editorDirty;
  }, [editorDirty]);

  const handleClose = useCallback(
    async (customisations?: DesignHuddleCustomisations) => {
      if (!editor) return setOpen(false);

      setOpen(false);
      setLoading(false);

      editor.remove();
      setEditor(undefined);

      if (!editorDirtyRef.current) return;

      onProgress?.(1);

      try {
        const [, publicRender] = await Promise.all([
          getDesignHuddleRender({
            getToken,
            accessToken: await getAccessToken([currentProject.id]),
            projectId: currentProject.id,
            pageNumber: currentPage,
            onProgress: content_type !== "video" ? onProgress : undefined,
          }),
          content_type === "video"
            ? getRenderUrlCloudflareStream({
                getToken,
                accessToken: await getAccessToken([currentProject.id]),
                projectId: currentProject.id,
                format: "mp4",
                filename: currentProject.id,
                onProgress,
              })
            : Promise.resolve(null),
        ]);

        onSave?.({
          page: currentPage,
          clonedProjectId: project_id !== currentProject.id ? currentProject.id : undefined,
          customisations,
          publicRenderUrl: publicRender?.success ? publicRender.url : undefined,
        });
        onProgress?.(null);
      } catch (error) {
        console.error("Error during Design Huddle render:", error);
        errorDialog.handleOpen();
      }
      setEditorDirty(false);
    },
    [
      editor,
      setOpen,
      getToken,
      getAccessToken,
      currentProject.id,
      currentPage,
      onSave,
      project_id,
      onProgress,
      errorDialog,
      content_type,
    ],
  );

  const handleContinue = useCallback(() => {
    if (!editor) return setOpen(false);

    setLoading(true);
    editor.getProjectData({}, (projectError, project) => {
      if (projectError) {
        errorDialog.handleOpen();
        return;
      }

      const page = project?.pages?.find(
        (p) => p.page_id === pages?.find((x) => x.page_number === (currentPage ?? 1))?.page_id,
      );
      const mergeTags = page?.elements
        ? extractMergeTags(
            Object.values(page.elements)
              .filter(isTextElement)
              .map((x) => x.text)
              .filter(notEmpty),
          )
        : [];
      if (mergeTags.length > 0) {
        publishTemplate(currentProject.id)
          .then((templateCode) =>
            setTimeout(() => {
              // oxlint-disable-next-line promise/no-nesting -- promise started in timer callback
              handleClose({ templateCode, mergeTags }).catch(console.error);
            }, 1500),
          )
          .catch((error: Error) => {
            console.error("Error publishing template:", error);
            errorDialog.handleOpen();
          });
      } else {
        setTimeout(() => {
          handleClose(undefined).catch(console.error);
        }, 1500);
      }
    });
  }, [
    editor,
    handleClose,
    publishTemplate,
    currentPage,
    currentProject,
    pages,
    setOpen,
    errorDialog,
  ]);

  const { loading: DHLoading, error: DHError } = useDesignHuddle([project_id]);

  useEffect(() => {
    if (DHError) {
      errorDialog.handleOpen();
      return;
    }
    if (DHLoading || errorDialog.open) return;
    if (currentProject.mustBeCloned) {
      cloneProject(currentProject.id, page_id)
        // oxlint-disable-next-line promise/always-return -- side-effect-only then() callback
        .then((newId) => {
          if (!newId) return;
          // oxlint-disable-next-line promise/no-nesting -- fire-and-forget side task, deliberately not chained
          void copyImage(getToken, imageURL(account_id, currentProject.id), newId).catch((error) =>
            console.error("Error copying image:", error),
          );
          setCurrentProject({ id: newId, mustBeCloned: false });
        })
        .catch((error) => console.error("Error cloning project:", error));
      return;
    }

    if (pages && !editorDirty) return;

    DSHDLib.getProject(currentProject.id, (projectErr, project) => {
      if (projectErr) {
        console.error("Error getting Design Huddle project:", projectErr);
        errorDialog.handleOpen();
        return;
      }

      setPages(project.pages);
      const page = project.pages?.find((p) => {
        if (!currentPage && page_id) {
          return p.page_id === page_id;
        }
        return currentPage && p.page_number === currentPage;
      });

      if (page) setCurrentPage(page.page_number);
    });
  }, [
    DHLoading,
    DHError,
    editor,
    currentPage,
    currentProject,
    cloneProject,
    account_id,
    getToken,
    page_id,
    errorDialog,
    editorDirty,
    pages,
  ]);

  useEffect(() => {
    if (!editor || errorDialog.open) return;

    editor.handlePageChange((_, { page_id: pageId }) => {
      setCurrentPage(pages?.find((p) => p.page_id === pageId)?.page_number);
      setEditorDirty(true);
    });
  }, [editor, pages, errorDialog]);

  const handleCloseErrorModal = useCallback(() => {
    errorDialog.handleClose();
    setOpen(false);
  }, [errorDialog, setOpen]);

  return (
    <>
      {!errorDialog.open && (
        <Dialog fullWidth maxWidth="xl" open={open} onClose={handleContinue}>
          <DialogTitle>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                data-analytics-id="design-huddle-editor-continue"
                type="submit"
                variant="contained"
                color="primary"
                loading={loading || !editor}
                onClick={handleContinue}
              >
                Continue
              </Button>
            </Stack>
          </DialogTitle>
          <DialogContent style={{ height: "75vh" }}>
            {!DHLoading && currentProject.id && !currentProject.mustBeCloned && (
              <DesignHuddleEditor
                onReady={setEditor}
                onChange={() => {
                  setEditorDirty(true);
                }}
                project_id={currentProject.id}
                page_number={currentPage}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
      {!errorDialog.open && <LoadingModal open={open && !editor} />}
      <ErrorModal open={errorDialog.open} handleClose={handleCloseErrorModal} />;
    </>
  );
};
